import React, { FunctionComponent, useState } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { Field, FieldProps } from 'formik';
import { Tabs, TabNavigation, Grid, Typography, Tooltip, Note, AnalyticsProps } from '@inplayer-org/inplayer-ui';
import { Moment } from 'moment';
import { MdInfoOutline } from 'react-icons/md';
import { FaPlus, FaMinus } from 'react-icons/fa';

// utils
import map from 'lodash/map';
import countries from 'data/countries';

// components
import CheckboxField from 'components/Fields/CheckboxField';
import Input from 'components/Fields/Input';
import Select from 'components/Fields/Select';
import FieldError from 'components/Fields/FieldError';
import StyledContainer from 'components/StyledContainer';
import SingleDayPicker from 'components/SingleDayPicker';
import TimeHourPicker from 'components/TimeHourPicker';
import Restricted from 'restrictions/roles/components/Restricted';

// images
import googleImg from 'assets/pics/integrations/google-play-logo.jpg';
import appleImg from 'assets/pics/integrations/app-store-logo.jpg';
import amazonImg from 'assets/pics/integrations/amazon-logo.png';
import rokuImg from 'assets/pics/integrations/roku.png';

// selectors
import { getActiveInAppIntegrationNames } from 'ducks/merchantDucks/inAppIntegrations/selectors';

// types
import RootState from 'ducks/RootState';
import { PaymentTypes } from 'pages/MerchantPages/Assets/Price/PriceContainer';

const { Cell, Container } = Grid;

const TooltipStyled = styled(Tooltip)`
  margin-left: 0.5rem;
`;

const Logo = styled.img`
  width: 5rem;
`;

const AlignedCell = styled(Cell)`
  vertical-align: middle;
`;

const DateAlignedCell = styled(AlignedCell)`
  margin-right: 1rem;
`;

const TooltipWrapper = styled.div`
  display: flex;
  > * {
    width: initial;
  }
`;

const MarginTooltip = styled(Tooltip)`
  margin-left: 0.3rem;
`;

enum GeoRestrictionType {
  Country = 0,
  CountrySet = 1,
}

const integrationImages = {
  googleInAppKeys: googleImg,
  appleInAppKeys: appleImg,
  amazonInAppKeys: amazonImg,
  rokuInAppKeys: rokuImg,
};

interface Country {
  code: string;
  name: string;
}

interface CountrySet {
  id: number;
  name: string;
}

interface StateProps {
  integrations: Array<string>;
}

interface OwnProps extends AnalyticsProps {
  countrySets: Array<CountrySet>;
  paymentType: string;
  geoRestrictionTypeTabIndex: number;
  isReadOnly: boolean;
}

type Props = OwnProps & StateProps;

// Restriction tabs
const restrictionTabs = [
  { title: 'Country', tag: 'link_country' },
  { title: 'Country set', tag: 'link_country_set' },
];

interface RestrictionTypeEntry {
  [key: string]: number;
}

const restrictionTypes: RestrictionTypeEntry = {
  whitelist: 0,
  blacklist: 1,
};

// Restriction types tabs (blacklist/whitelist)
const restrictionTypeTabs = [
  {
    name: 'allow', // whitelist
    icon: <FaPlus />,
    tag: 'button_allow',
  },
  {
    name: 'deny', // blacklist
    icon: <FaMinus />,
    tag: 'button_deny',
  },
];

// Country set options
const createCountrySetsOptions = (countrySetsArray: Array<CountrySet>) => {
  return map(countrySetsArray, (country) => ({
    value: country.id.toString(),
    displayName: country.name,
    tag: country.name,
  }));
};

// Country options
const createCountryOptions = (countriesArray: Array<Country>) => {
  return map(countriesArray, (country) => ({
    value: country.code,
    displayName: country.name,
    tag: country.name,
  }));
};

const AdditionalPriceInfo: FunctionComponent<Props> = ({
  countrySets,
  paymentType,
  geoRestrictionTypeTabIndex,
  integrations,
  isReadOnly,
}) => {
  const [geoRestrictionTabIndex, setGeoRestrictionTab] = useState(geoRestrictionTypeTabIndex);

  const handleTabChange = (setFieldValue: (fieldName: string, value: any) => void) => (index: number) => {
    const newFieldValue = Object.keys(restrictionTypes)[index];
    setFieldValue('restrictionType', newFieldValue);
  };

  const handleRestrictionTypeChanged = (setFieldValue: (name: string, value: any) => void) => (tabIndex: number) => {
    setGeoRestrictionTab(tabIndex);
    setFieldValue('geoRestrictionType', tabIndex);
  };

  const renderInAppIntegrations = () => {
    if (!integrations.length) {
      return (
        <>
          <Note
            text=""
            title="Please integrate your in-app platform account so you can set up in-app purchases."
            type="warning"
          />
          <Typography variant="p">
            {`You can set up your in-app integrations `}
            <NavLink to="/settings/integrations/in-app-integrations">here</NavLink>
          </Typography>
        </>
      );
    }
    return integrations.map((element) => {
      return integrationImages[element] ? (
        <StyledContainer key={element} margin="0.5rem 0 0 0">
          <Cell width={2} middle center>
            <Logo src={integrationImages[element]} alt={element} />
          </Cell>
          <Cell width={3}>
            <Field name={element} type="text" component={Input} disabled={isReadOnly} />
            <FieldError name={element} />
          </Cell>
        </StyledContainer>
      ) : null;
    });
  };

  const renderCountrySetRestrictionContent = () => {
    return (
      <>
        <Container columns={4}>
          <Cell width={2}>
            <Field name="countrySet" isInlineLabelField>
              {({ field, form, meta }: FieldProps) => {
                return (
                  <Select
                    tag="dropdown_country_set"
                    field={field}
                    form={form}
                    meta={meta}
                    value={field.value}
                    defaultOption={{ displayName: 'Select country set' }}
                    options={createCountrySetsOptions(countrySets)}
                    disabled={isReadOnly}
                  />
                );
              }}
            </Field>
          </Cell>
        </Container>
        <Container columns={4}>
          <Cell width={2}>
            <NavLink to="/settings/auth/geo-restrictions">Create a new country set &rarr;</NavLink>
          </Cell>
        </Container>
      </>
    );
  };

  const renderCountryRestrictionContent = (
    restrictionType: string | null,
    setFieldValue: (fieldName: string, value: any) => void
  ) => {
    if (!restrictionType) {
      setFieldValue('restrictionType', 'whitelist');
    }

    const tabIndex = restrictionTypes[restrictionType!];

    return (
      <Container columns={12}>
        <Cell width={6}>
          <Field name="country">
            {({ field, form, meta }: FieldProps) => {
              return (
                <Select
                  tag="dropdown_country"
                  field={field}
                  form={form}
                  meta={meta}
                  value={field.value}
                  defaultOption={{ displayName: 'Select country' }}
                  options={createCountryOptions(countries)}
                  disabled={isReadOnly}
                />
              );
            }}
          </Field>
        </Cell>
        <Cell width={6}>
          <Tabs tabs={restrictionTypeTabs} selectedTabIndex={tabIndex} onTabClick={handleTabChange(setFieldValue)} />
        </Cell>
      </Container>
    );
  };

  return (
    <StyledContainer margin="2rem 0 0 0" rowGap="1.6rem" columns={1}>
      {(paymentType === PaymentTypes.Subscription || paymentType === PaymentTypes.Season) && (
        <>
          {/* Free trial */}
          <Container rowGap="1rem" columns={1}>
            <Cell width={1}>
              <Field name="isFreeTrialEnabled">
                {({ field, form }: any) => {
                  return (
                    <>
                      <CheckboxField
                        tag="checkbox_free_trial"
                        id="isFreeTrialEnabled"
                        name="isFreeTrialEnabled"
                        label="Free-trial"
                        inlineLabel
                        checked={field.value}
                        onCheck={(val) => form.setFieldValue('isFreeTrialEnabled', val)}
                        disabled={isReadOnly}
                      />
                      <br />
                      {field.value && (
                        <Cell width={1}>
                          <Container columns={8}>
                            <Cell width={2} middle>
                              <Field
                                tag="text_input_free_trial_days"
                                name="freeTrialDays"
                                type="text"
                                component={Input}
                                disabled={isReadOnly}
                              />
                              <FieldError name="freeTrialDays" />
                            </Cell>
                            <Cell width={1} middle>
                              <Typography variant="p">days</Typography>
                            </Cell>
                          </Container>
                        </Cell>
                      )}
                    </>
                  );
                }}
              </Field>
            </Cell>
          </Container>

          {/* Additional fee */}
          <Container rowGap="1rem" columns={1}>
            <Cell width={1}>
              <Field name="isAdditionalFeeEnabled">
                {({ field, form }: any) => {
                  return (
                    <>
                      <TooltipWrapper>
                        <CheckboxField
                          tag="checkbox_additional_fee"
                          id="isAdditionalFeeEnabled"
                          name="isAdditionalFeeEnabled"
                          label="Additional fee"
                          inlineLabel
                          checked={field.value}
                          onCheck={(val) => form.setFieldValue('isAdditionalFeeEnabled', val)}
                          disabled={isReadOnly}
                        />
                        <MarginTooltip
                          tag="tooltip_additional_fee"
                          placement="right"
                          content={`Set up a fee customers will have to pay on their first subscription payment, \nin addition to the standard subscription price.`}
                        >
                          <MdInfoOutline />
                        </MarginTooltip>
                      </TooltipWrapper>
                      <br />
                      {field.value && (
                        <Cell width={1}>
                          <Container rowGap="1rem">
                            <Cell width={12} middle>
                              <Typography variant="p">Amount:</Typography>
                            </Cell>
                            <Cell width={3}>
                              <Field
                                tag="text_input_addtional_fee_amount"
                                name="additionalFeeAmount"
                                type="text"
                                component={Input}
                                disabled={isReadOnly}
                              />
                              <FieldError name="additionalFeeAmount" />
                            </Cell>
                            <Cell width={1} middle>
                              <Typography variant="p">{form.values.currency}</Typography>
                            </Cell>
                            <Cell width={12} middle>
                              Description:
                            </Cell>
                            <Cell width={3} middle>
                              <Field
                                tag="text_input_addtional_fee_description"
                                name="additionalFeeDescription"
                                type="text"
                                component={Input}
                                disabled={isReadOnly}
                              />
                              <FieldError name="additionalFeeDescription" />
                            </Cell>
                          </Container>
                        </Cell>
                      )}
                    </>
                  );
                }}
              </Field>
            </Cell>
          </Container>
        </>
      )}
      {paymentType === PaymentTypes.Ppv && (
        // Schedule start
        <Container rowGap="1rem" columns={1}>
          <Cell width={1}>
            <Field name="isScheduleStartEnabled">
              {({ field, form }: any) => {
                return (
                  <>
                    <TooltipWrapper>
                      <CheckboxField
                        tag="checkbox_scheduled_start"
                        id="isScheduleStartEnabled"
                        name="isScheduleStartEnabled"
                        label="Schedule start"
                        inlineLabel
                        checked={field.value}
                        onCheck={(val) => form.setFieldValue('isScheduleStartEnabled', val)}
                        disabled={isReadOnly}
                      />
                      <MarginTooltip
                        tag="tooltip_scheduled_start"
                        placement="right"
                        content={`Leave this blank if you want access to the content to start right after purchase. \nOtherwise, set up a date and time on which the access should start after purchase. \nNote that changing this option after customers have already made their purchase \nwill not change the access start for those customers.`}
                      >
                        <MdInfoOutline />
                      </MarginTooltip>
                    </TooltipWrapper>
                    <br />
                    {field.value && (
                      <Cell width={1}>
                        <Container rowGap="1rem">
                          <Cell width={10}>
                            <Field name="startsAt">
                              {({ field: childField, form: childForm }: any) => {
                                return (
                                  <>
                                    <DateAlignedCell width={2} middle>
                                      <SingleDayPicker
                                        date={childField.value}
                                        handleDateChange={(date: Moment) => {
                                          childForm.setFieldValue(childField.name, date);
                                        }}
                                        disabled={isReadOnly}
                                        disablePastDays
                                      />
                                    </DateAlignedCell>
                                    <AlignedCell width={2} middle>
                                      <TimeHourPicker
                                        handleTimeChange={(time: Moment) => {
                                          childForm.setFieldValue(childField.name, time);
                                        }}
                                        time={childField.value}
                                        disabled={isReadOnly}
                                      />
                                    </AlignedCell>
                                    <Restricted isRestricted={isReadOnly}>
                                      <AlignedCell width={1} middle>
                                        <Container columns={1}>
                                          <Cell width={1} middle>
                                            <TooltipStyled placement="top" content="Please enter your local time.">
                                              <MdInfoOutline />
                                            </TooltipStyled>
                                          </Cell>
                                        </Container>
                                      </AlignedCell>
                                    </Restricted>
                                  </>
                                );
                              }}
                            </Field>
                          </Cell>
                        </Container>
                      </Cell>
                    )}
                  </>
                );
              }}
            </Field>
          </Cell>
        </Container>
      )}

      {/* Restrictions */}
      <Container rowGap="1rem" columns={1}>
        <Cell width={1}>
          <Field name="isGeoRestrictionEnabled">
            {({ field, form }: any) => {
              const {
                values: { restrictionType },
                setFieldValue,
              } = form;

              return (
                <>
                  <TooltipWrapper>
                    <CheckboxField
                      tag="checkbox_geo_restrictions"
                      id="geoRestriction"
                      name="isGeoRestrictionEnabled"
                      label="Geo-restriction"
                      inlineLabel
                      checked={field.value}
                      onCheck={(val) => form.setFieldValue('isGeoRestrictionEnabled', val)}
                      disabled={isReadOnly}
                    />
                    <MarginTooltip
                      tag="tooltip_geo_restrictions"
                      placement="right"
                      content={`Make this price option available only in select counties. \nTo set up geo-restrictions for the whole asset instead, \ngo to the Restictions section.`}
                    >
                      <MdInfoOutline />
                    </MarginTooltip>
                  </TooltipWrapper>
                  <br />
                  {field.value && (
                    <Cell width={1}>
                      <Container columns={1} rowGap="1.5rem">
                        <Container columns={4}>
                          <Cell width={2}>
                            <TabNavigation
                              tabs={restrictionTabs}
                              selectedTabIndex={geoRestrictionTabIndex}
                              onTabClick={handleRestrictionTypeChanged(setFieldValue)}
                            />
                          </Cell>
                        </Container>
                        {geoRestrictionTabIndex === GeoRestrictionType.Country
                          ? renderCountryRestrictionContent(restrictionType, setFieldValue)
                          : renderCountrySetRestrictionContent()}
                      </Container>
                    </Cell>
                  )}
                </>
              );
            }}
          </Field>
        </Cell>
      </Container>

      {/* InApp Product Id */}
      <Container rowGap="1rem" columns={1}>
        <Cell width={1}>
          <Field name="isExternalInAppEnabled">
            {({ field, form }: any) => {
              return (
                <>
                  <CheckboxField
                    tag="checkbox_use_external_inapp_ids"
                    id="isExternalInAppEnabled"
                    name="isExternalInAppEnabled"
                    label="Use external In-App IDs"
                    inlineLabel
                    checked={field.value}
                    onCheck={(val) => form.setFieldValue('isExternalInAppEnabled', val)}
                    disabled={isReadOnly}
                  />
                  <br />
                  {field.value && <Cell width={1}>{renderInAppIntegrations()}</Cell>}
                </>
              );
            }}
          </Field>
        </Cell>
      </Container>
    </StyledContainer>
  );
};

const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state: RootState) => ({
  integrations: getActiveInAppIntegrationNames(state),
});

export default connect(mapStateToProps)(AdditionalPriceInfo);
